import {makeTrackEvent} from './makeTrackEvent';

export type ForgeNotebookAction =
  | 'created'
  | 'deleted'
  | 'opened'
  | 'opened_in_artifacts'
  | 'renamed';

export type ForgeNotebookLocation =
  | 'forge_page'
  | 'notebook_page'
  | 'project_page'
  | 'team_page';

export type ForgeNotebookSource =
  | {
      notebook_source_id: null;
      notebook_source_type: 'blank';
    }
  | {
      notebook_source_id: string;
      notebook_source_type: 'duplicate';
    }
  | {
      notebook_source_id: string;
      notebook_source_type: 'quickstart';
    };

type ForgeNotebookEvent = {
  location: ForgeNotebookLocation;
  notebook_id: string;
};

export type ForgeNotebookInteractionEvent =
  | (ForgeNotebookEvent & ForgeNotebookSource & {action: 'created'})
  | (ForgeNotebookEvent & {
      action: Exclude<ForgeNotebookAction, 'created'>;
    });

export const FORGE_NOTEBOOK_BLANK_SOURCE = {
  notebook_source_id: null,
  notebook_source_type: 'blank',
} satisfies ForgeNotebookSource;

/** A new notebook starts blank unless it copies a quickstart template. */
export function getNotebookSource(
  quickstartName?: string
): Exclude<ForgeNotebookSource, {notebook_source_type: 'duplicate'}> {
  if (quickstartName == null) {
    return FORGE_NOTEBOOK_BLANK_SOURCE;
  }

  return {
    notebook_source_id: quickstartName,
    notebook_source_type: 'quickstart',
  };
}

type AnnotatedForgeNotebookInteractionEvent = {
  action: ForgeNotebookAction;
  location: ForgeNotebookLocation;
  notebook_id: string;
  notebook_source_id: string | null;
  notebook_source_type: ForgeNotebookSource['notebook_source_type'] | null;
};

const trackForgeNotebookInteractionEvent = makeTrackEvent<
  AnnotatedForgeNotebookInteractionEvent,
  {
    action: {
      description: 'the notebook interaction that completed';
      exampleValues: [
        'created',
        'deleted',
        'opened',
        'opened_in_artifacts',
        'renamed',
      ];
    };
    location: {
      description: 'the page where the interaction started';
      exampleValues: [
        'forge_page',
        'notebook_page',
        'project_page',
        'team_page',
      ];
    };
    notebook_id: {
      description: 'the interacted-with notebook, or the new notebook for created events';
    };
    notebook_source_id: {
      description: 'the quickstart name or duplicated notebook id, or null when there is no source';
    };
    notebook_source_type: {
      description: 'how a notebook was created, or null for non-created actions';
      exampleValues: ['blank', 'duplicate', 'quickstart', null];
    };
    _description: 'Tracks creation and management interactions with Forge notebooks';
    _location: 'Project notebooks, team notebooks, Forge notebooks, and the notebook editor page';
    _motivation: 'Measure how notebooks are created, opened, and managed across their entry points';
  }
>('Forge notebooks interaction');

export const trackForgeNotebookInteraction = (
  event: ForgeNotebookInteractionEvent
): void => {
  if (event.action === 'created') {
    trackForgeNotebookInteractionEvent(event);
    return;
  }

  trackForgeNotebookInteractionEvent({
    ...event,
    notebook_source_id: null,
    notebook_source_type: null,
  });
};
